// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Viewer tag engine: leader lines rendered as cylinder meshes in an overlay scene,
 * camera-synced projection, and world-space head updates on drag commit.
 *
 * Label HTML and all pointer interactions are owned by React (`TagLabelsOverlay`).
 * React notifies this class at drag lifecycle boundaries so it can:
 *   - lock/unlock viewer navigation
 *   - suppress/rebuild leader geometry
 *   - convert client coordinates to world space on commit
 *   - persist placement
 */

import type { AutodeskElementTag, AutodeskElementTagId, AutodeskExternalId } from '@evolvemep/api-ts';

import type { APSViewerStore } from 'components/Autodesk/extensions/APSViewerStoreExtension/createAPSViewerStore';
import type { ElementTagsStore } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createElementTagsStore';
import type { RenderedTagsStore } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createRenderedTagsStore';
import {
  clientToWorldOnCameraPlane,
  computeDefaultHead,
  getElementAnchor,
  sortPropertiesComp,
  updateElbowLeaderMeshes,
} from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/helpers';
import type {
  SelectedElementProperty,
  TagPropertyValue,
} from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/types';
import { addViewerListener, monitorStoreState } from 'components/Autodesk/extensions/utils';
import { getBulkPropertiesBatched } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE, toPoint3D, toVector3, type Point3D, type ViewerCamera } from 'components/Autodesk/viewerThree';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';

const TAG_SCENE_NAME = 'element-tags';
const TAG_DEFAULT_HEAD_OFFSET_FT = 1.5;
const TAG_LEADER_COLOR = labelTheming.colors.primary;
const TAG_LEADER_RADIUS_FT = 0.004;
const TAG_LEADER_SHOULDER_LENGTH_PX = 24;
const TAG_LEADER_MIN_PIXEL_WIDTH = 2;

/**
 * Represents the state of a single tag, including some things react shouldn't be concerned with such as leader meshes.
 */
type RenderedTagState = {
  /** The tag's external ID. */
  externalId: AutodeskExternalId;
  dbId: AutodeskDbId | null;
  /** The part of the tag's leader line that connects to the element. */
  anchor: THREE.Vector3 | null;
  /** The default head position for a tag with no placement. */
  fallbackHead: Point3D | null;
  /** The part of the tag's leader line that connects to the tag. */
  head: Point3D | null;
  /**
   * The tag's 2 leader-segment meshes, created once and kept for the tag's lifetime.
   * An elbow leader uses both; a single-segment (or hidden) leader just hides the unused one.
   */
  leaderMeshes: [THREE.Mesh, THREE.Mesh];
  /** `true` while a React-driven drag is in progress; suppresses leader geometry. */
  isDragging: boolean;
  /**
   * The tag's property names paired with their resolved values.
   *
   * Precomputed here rather than in `toRenderedTagsSnapshot` because that runs on every camera
   * frame, and sorting plus a cache lookup per property per tag would be wasted work at that rate.
   *
   * @remarks `null` while loading
   */
  propertyValues: readonly TagPropertyValue[] | null;
};

export class TagRenderingManager {
  private readonly viewer: Autodesk.Viewing.GuiViewer3D;

  private readonly leaderMaterial: THREE.MeshBasicMaterial;
  /**
   * Unit cylinder (radius 1, height 1, axis along Y) shared by every leader segment mesh.
   * Segments are drawn by scaling/positioning/rotating this single geometry instance rather than
   * creating a new one per segment, so nothing needs to be recreated or disposed on every update.
   */
  private readonly leaderGeometry: THREE.CylinderGeometry;
  private readonly onCameraChanged: () => void;

  private readonly viewerStore: APSViewerStore;
  private readonly elementTagsStore: ElementTagsStore;
  private readonly renderedTagsStore: RenderedTagsStore;

  private cleanUpFns: (() => void)[] = [];

  private tagState = new Map<AutodeskElementTagId, RenderedTagState>();

  /**
   * Every key that has been through a completed lookup, whether or not the element turned out to carry
   * the property.
   */
  private readonly lookedUpPropertyKeys = new Set<ReturnType<typeof this.propertyCacheKey>>();
  /**
   * Cached property values, keyed by {@link propertyCacheKey}.
   */
  private readonly propertyCache = new Map<ReturnType<typeof this.propertyCacheKey>, Autodesk.Viewing.Property>();

  /**
   * `true` once `dispose()` has run. Every public method no-ops after this to guard against calls that resolve late
   * (e.g. an in-flight mutation finishing after unload).
   **/
  private disposed = false;

  constructor(
    viewer: Autodesk.Viewing.GuiViewer3D,
    viewerStore: APSViewerStore,
    elementTagsStore: ElementTagsStore,
    renderedTagsStore: RenderedTagsStore,
  ) {
    this.viewer = viewer;
    this.viewerStore = viewerStore;
    this.elementTagsStore = elementTagsStore;
    this.renderedTagsStore = renderedTagsStore;

    this.ensureOverlayScene = this.ensureOverlayScene.bind(this);
    this.onDraftTagsUpdated = this.onDraftTagsUpdated.bind(this);
    this.onExternalIdsStale = this.onExternalIdsStale.bind(this);

    this.leaderMaterial = new THREE.MeshBasicMaterial({
      color: TAG_LEADER_COLOR,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
    });
    this.leaderGeometry = new THREE.CylinderGeometry(1, 1, 1, 6, 1, false);

    this.onCameraChanged = () => this.updateAllTags();
    const onVisibilityChanged = () => this.updateAllTags();

    this.cleanUpFns.push(
      addViewerListener(viewer, Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged),
      addViewerListener(viewer, Autodesk.Viewing.HIDE_EVENT, onVisibilityChanged),
      addViewerListener(viewer, Autodesk.Viewing.SHOW_EVENT, onVisibilityChanged),
      addViewerListener(viewer, Autodesk.Viewing.ISOLATE_EVENT, onVisibilityChanged),
      /** Monitor for state changes that may affect visibility. */
      monitorStoreState(elementTagsStore, ({ tagsVisible }) => tagsVisible, onVisibilityChanged),
      /** Monitor for state changes to the tags, or the external IDs used to derive their data */
      monitorStoreState(elementTagsStore, ({ draftTags }) => draftTags, this.onDraftTagsUpdated),
      monitorStoreState(viewerStore, ({ externalIdLookup }) => externalIdLookup, this.onExternalIdsStale),
    );

    if (isNotNil(this.viewer.model)) {
      this.ensureOverlayScene();
    } else {
      this.cleanUpFns.push(
        addViewerListener(viewer, Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, this.ensureOverlayScene),
      );
    }
  }

  private ensureOverlayScene() {
    const { impl } = this.viewer;
    if (!impl.overlayScenes || !(TAG_SCENE_NAME in impl.overlayScenes)) {
      impl.createOverlayScene(TAG_SCENE_NAME);
    }
  }

  get tagsVisible(): boolean {
    return this.elementTagsStore.getSnapshot().tagsVisible;
  }

  get draftTags() {
    return this.elementTagsStore.getSnapshot().draftTags;
  }

  get externalIdLookup() {
    return this.viewerStore.getSnapshot().externalIdLookup;
  }

  private getAnchor(dbId: AutodeskDbId | null) {
    return isNotNil(dbId) ? getElementAnchor(this.viewer.model, dbId) : null;
  }

  /** Create a tag's fixed pair of leader-segment meshes, hidden until the first `updateTagVisuals` call. */
  private createLeaderMeshes(): [THREE.Mesh, THREE.Mesh] {
    const meshA = new THREE.Mesh(this.leaderGeometry, this.leaderMaterial);
    const meshB = new THREE.Mesh(this.leaderGeometry, this.leaderMaterial);
    meshA.visible = false;
    meshB.visible = false;
    return [this.addToOverlay(meshA), this.addToOverlay(meshB)];
  }

  private removeLeaderMeshes(meshes: [THREE.Mesh, THREE.Mesh]) {
    for (const mesh of meshes) {
      this.viewer.impl.removeOverlay(TAG_SCENE_NAME, mesh);
    }
    this.viewer.impl.invalidate(true, true, true);
  }

  private propertyCacheKey(dbId: AutodeskDbId, propName: SelectedElementProperty['name']) {
    return `${dbId}\0${propName}` as const;
  }

  /**
   * Build a tag's display-ordered property values from {@link propertyCache}, or `null` if any of them
   * has not been looked up yet.
   *
   * @returns `null` when any properties are not yet cached (or `dbID` is nil)
   */
  private buildPropertyValues(
    dbId: AutodeskDbId | null,
    propNames: readonly SelectedElementProperty['name'][] | undefined = [],
  ): readonly TagPropertyValue[] | null {
    if (isNil(dbId)) return null;
    // Checked before sorting, because incomplete is the common case on the paths that build twice — a
    // lookup arriving, or a property being added — and the sorted list would just be thrown away.
    if (propNames.some((propName) => !this.lookedUpPropertyKeys.has(this.propertyCacheKey(dbId, propName)))) {
      return null;
    }
    return propNames
      .toSorted(sortPropertiesComp)
      .map((propName) => [propName, this.propertyCache.get(this.propertyCacheKey(dbId, propName)) ?? null] as const);
  }

  private initializeTagState(tag: AutodeskElementTag): RenderedTagState {
    const dbId = this.externalIdLookup?.[tag.elementId] ?? null;
    const anchor = this.getAnchor(dbId);
    const fallbackHead = computeDefaultHead(this.viewer, anchor, TAG_DEFAULT_HEAD_OFFSET_FT);
    return {
      dbId,
      anchor,
      fallbackHead,
      externalId: tag.elementId,
      head: tag.placement ?? fallbackHead,
      leaderMeshes: this.createLeaderMeshes(),
      isDragging: false,
      propertyValues: this.buildPropertyValues(dbId, tag.properties),
    };
  }

  /**
   * Re-derive the store-owned half of a tag's state, keeping only the fields this class owns
   * (`leaderMeshes`, `isDragging`) and the viewer-derived ones (`dbId`, `anchor`, `fallbackHead`).
   */
  private deriveTagStateFromStore(tag: AutodeskElementTag, lastState: RenderedTagState): RenderedTagState {
    return {
      ...lastState,
      head: tag.placement ?? lastState.fallbackHead,
      propertyValues:
        this.buildPropertyValues(lastState.dbId, tag.properties) ??
        // fall back to the last complete set while the new one resolves
        lastState.propertyValues,
    };
  }

  private updateTagDragging(lastState: RenderedTagState, isDragging: boolean): RenderedTagState {
    return {
      ...lastState,
      isDragging,
    };
  }

  /** Used to update each element tag's state whenever the external ID lookup changes. */
  private handleStaleModelData(elementTagId: AutodeskElementTagId, lastState: RenderedTagState): RenderedTagState {
    const dbId = this.externalIdLookup?.[lastState.externalId] ?? null;
    const anchor = this.getAnchor(dbId);
    const fallbackHead = computeDefaultHead(this.viewer, anchor, TAG_DEFAULT_HEAD_OFFSET_FT);
    const storeTag = this.draftTags?.find((tag) => tag.elementTagId === elementTagId);
    return {
      ...lastState,
      dbId,
      anchor,
      fallbackHead,
      // The anchor moved, so an unplaced tag's fallback head moves with it. Re-derive `head` from the
      // store rather than reusing `lastState.head`, for the same reason `deriveTagStateFromStore` does.
      head: storeTag?.placement ?? fallbackHead,
      // Rebuilt against the *new* dbId — the previous values belong to the element the old dbId
      // pointed at, which is not necessarily this tag's element anymore.
      propertyValues: this.buildPropertyValues(dbId, storeTag?.properties),
    };
  }

  private removeTagState(tagState: RenderedTagState) {
    if (this.disposed) return;

    // If this tag was mid-drag, release navigation lock.
    if (tagState.isDragging) this.viewer.setNavigationLock(false);

    this.removeLeaderMeshes(tagState.leaderMeshes);
  }

  /** Check if the external ID lookup has changed. If it has, all tag data related to the model is stale. Update it. */
  private onExternalIdsStale() {
    for (const [elementTagId, tag] of this.tagState) {
      this.tagState.set(elementTagId, this.handleStaleModelData(elementTagId, tag));
    }
    // Anchors (and so leader geometry and projected label positions) just changed, so republish
    // instead of waiting for an unrelated camera or store change to notice.
    this.updateAllTags();
    // dbIds only become resolvable once the `externalIdLookup` arrives, so this is the first point at which most
    // tags can have their property values looked up at all.
    void this.fetchUncachedPropertyValues();
  }

  private republishTags() {
    this.renderedTagsStore.setTags(this.toRenderedTagsSnapshot());
  }

  /** Each tag's selected property names, as the store has them at the moment of the call. */
  private getDraftPropNamesByTagId() {
    return new Map(this.draftTags?.map((tag) => [tag.elementTagId, tag.properties]));
  }

  /**
   * Look up every tag property value that is not cached yet in one batched request, then republish the
   * tags it covered.
   *
   * @remarks Tags are otherwise kept current by {@link buildPropertyValues} at each state-derivation site.
   */
  private async fetchUncachedPropertyValues() {
    const model = this.viewer.model;
    if (isNil(model)) return;

    const propNamesByTagId = this.getDraftPropNamesByTagId();

    const missingDbIds = new Set<AutodeskDbId>();
    const missingPropNames = new Set<SelectedElementProperty['name']>();
    for (const [elementTagId, { dbId }] of this.tagState) {
      if (isNil(dbId)) continue;
      for (const propName of propNamesByTagId.get(elementTagId) ?? []) {
        if (this.lookedUpPropertyKeys.has(this.propertyCacheKey(dbId, propName))) continue;
        missingDbIds.add(dbId);
        missingPropNames.add(propName);
      }
    }
    if (missingDbIds.size === 0 || missingPropNames.size === 0) return;

    const dbIds = [...missingDbIds];
    const propFilter = [...missingPropNames];
    const results = await getBulkPropertiesBatched(model, dbIds, { propFilter });
    if (this.disposed) return;

    // The whole requested cross-product has now been asked about, including the dbIds the property
    // database could not resolve and the properties an element does not carry, both of which it simply
    // omits from the response. Recording that here is what stops them being re-requested next lookup.
    for (const dbId of dbIds) {
      for (const propName of propFilter) {
        this.lookedUpPropertyKeys.add(this.propertyCacheKey(dbId, propName));
      }
    }
    for (const { dbId, properties } of results) {
      for (const propName of propFilter) {
        const match = properties.find((property) => property.displayName === propName);
        if (isNotNil(match)) this.propertyCache.set(this.propertyCacheKey(dbId, propName), match);
      }
    }

    // Re-read the store instead of reusing the pre-flight `propNamesByTagId`;
    // a tag's properties can change while the lookup is in flight
    const latestPropNamesByTagId = this.getDraftPropNamesByTagId();
    for (const [elementTagId, tagState] of this.tagState) {
      // Only the requested dbIds had anything written for them, so no other tag can have changed.
      if (isNil(tagState.dbId) || !missingDbIds.has(tagState.dbId)) continue;
      this.tagState.set(elementTagId, {
        ...tagState,
        propertyValues:
          this.buildPropertyValues(tagState.dbId, latestPropNamesByTagId.get(elementTagId)) ?? tagState.propertyValues,
      });
    }
    this.republishTags();
  }

  /** Called whenever the `draftTags` update. the tag list, properties, and placement. */
  private onDraftTagsUpdated() {
    if (this.disposed) return;
    const storeTagIds = new Set(this.draftTags?.map((tag) => tag.elementTagId));
    for (const [elementTagId, tagState] of this.tagState) {
      if (!storeTagIds.has(elementTagId)) {
        this.removeTagState(tagState);
        this.tagState.delete(elementTagId);
        // `propertyCache` is intentionally not pruned here — it is keyed by dbId, so its entries stay
        // valid and reusable if this element is tagged again.
      }
    }
    for (const tag of this.draftTags ?? []) {
      const lastState = this.tagState.get(tag.elementTagId);
      if (isNil(lastState)) {
        this.tagState.set(tag.elementTagId, this.initializeTagState(tag));
      } else {
        this.tagState.set(tag.elementTagId, this.deriveTagStateFromStore(tag, lastState));
      }
    }

    this.updateAllTags();
    void this.fetchUncachedPropertyValues();
  }

  /** Returns `false` when the element for `dbId` is hidden or isolated away in the viewer. */
  private isDbIdVisible(dbId: number): boolean {
    // `viewer.model` can be momentarily undefined while a model swaps (same guard used in helpers.ts).
    if (isNil(this.viewer.model)) return false;
    return this.viewer.isNodeVisible(dbId, this.viewer.model);
  }

  private addToOverlay(mesh: THREE.Mesh): THREE.Mesh {
    this.viewer.impl.addOverlay(TAG_SCENE_NAME, mesh);
    return mesh;
  }

  /** Helper to determine if a single tag should render.*/
  private checkIfTagIsVisible({ dbId, anchor, head }: RenderedTagState, projected: THREE.Vector3): boolean {
    return (
      this.tagsVisible &&
      isNotNil(dbId) &&
      isNotNil(anchor) &&
      isNotNil(head) &&
      this.isDbIdVisible(dbId) &&
      projected.z >= -1 &&
      projected.z <= 1
    );
  }

  /**
   * Manages the leader-line visuals for a single tag: shows/hides its 2 leader meshes and, when
   * visible, updates their scale/position/rotation in place. Never creates, destroys, or disposes
   * meshes/geometry — that only happens when the tag itself is added or removed.
   * Does NOT notify the `RenderedTagsStore` — callers must call `republishTags` after batching.
   *
   * The leader is laid out from `tagState.head` — the same value `toRenderedTagsSnapshot` publishes as the
   * label's position — so the two are always consistent within a single update, with no dependency
   * on when React commits.
   */
  private updateTagVisuals(tagState: RenderedTagState) {
    const [meshA, meshB] = tagState.leaderMeshes;

    if (!this.tagsVisible) {
      meshA.visible = false;
      meshB.visible = false;
      this.viewer.impl.invalidate(true, true, true);
      return;
    }

    const camera = this.viewer.impl.camera as ViewerCamera;
    const projected = toVector3(tagState.head).project(camera);
    // We want to keep leader lines visible even when the tag is off-screen,
    // so the user can see there's a tag in that direction and pan to it.
    const canRenderLeader = this.checkIfTagIsVisible(tagState, projected);

    if (!canRenderLeader || tagState.isDragging) {
      meshA.visible = false;
      meshB.visible = false;
      this.viewer.impl.invalidate(true, true, true);
      return;
    }

    updateElbowLeaderMeshes(
      this.viewer,
      // In reality, these will never be null because `checkIfTagIsVisible` had to be true past this point,
      // and that requires `anchor` and `head` to be non-null. But TypeScript doesn't know that, so we have to coerce them.
      tagState.anchor ?? new THREE.Vector3(),
      tagState.head ?? new THREE.Vector3(),
      tagState.leaderMeshes,
      TAG_LEADER_RADIUS_FT,
      TAG_LEADER_SHOULDER_LENGTH_PX,
      TAG_LEADER_MIN_PIXEL_WIDTH,
    );
    this.viewer.impl.invalidate(true, true, true);
  }

  /**
   * Project all tags to pixel space and push to the store so React can re-render labels.
   * `visible` here gates the label div only (see `TagLabel`) — it does NOT gate the leader
   * line, which `updateTagVisuals` keeps rendering even when the head is off-screen.
   */
  private toRenderedTagsSnapshot() {
    const camera = this.viewer.impl.camera as ViewerCamera;
    const { clientWidth: width, clientHeight: height } = this.viewer.container;
    const storeTagIds = new Set(this.draftTags?.map((tag) => tag.elementTagId));

    return [...this.tagState.entries()].map(([elementTagId, tagState]) => {
      const projected = toVector3(tagState.head).project(camera);
      const visible =
        this.checkIfTagIsVisible(tagState, projected) &&
        storeTagIds.has(elementTagId) &&
        Math.abs(projected.x) <= 1.2 &&
        Math.abs(projected.y) <= 1.2;

      return {
        elementTagId,
        visible,
        externalId: tagState.externalId,
        propertyValues: tagState.propertyValues,
        x: ((projected.x + 1) * width) / 2,
        y: ((-projected.y + 1) * height) / 2,
      };
    });
  }

  /**
   * Push a tag update to both consumers together: leader meshes in the overlay scene, and projected
   * label positions to React. Both are computed from the same `tagState` in the same tick, which is
   * what keeps the leader and the label from ever disagreeing.
   */
  private updateAllTags() {
    for (const tagState of this.tagState.values()) this.updateTagVisuals(tagState);
    this.republishTags();
  }

  // region Public API drag lifecycle
  /**
   * Called when the drag threshold is crossed on a label.
   * Locks viewer navigation and suppresses leaders while the user is dragging.
   */
  public onLabelDragStart(elementTagId: AutodeskElementTagId) {
    if (this.disposed) return;
    const tagState = this.tagState.get(elementTagId);
    if (isNil(tagState)) return;
    const nextTagState = this.updateTagDragging(tagState, true);
    this.tagState.set(elementTagId, nextTagState);

    this.viewer.setNavigationLock(true);
    this.updateTagVisuals(nextTagState);
    this.republishTags();
  }

  /**
   * Called on pointer-up after a drag. Converts client coordinates to a world-space head,
   * updates the tag, rebuilds leaders, and fires the persistence callback.
   *
   * @param elementTagId - ID of the tag being dragged
   * @param clientX  - pointer client X at release
   * @param clientY  - pointer client Y at release
   * @param grabOffsetX - horizontal offset from label center to the pointer at drag start
   * @param grabOffsetY - vertical offset from label center to the pointer at drag start
   */
  public onLabelDragCommit(
    elementTagId: AutodeskElementTagId,
    clientX: number,
    clientY: number,
    grabOffsetX: number,
    grabOffsetY: number,
  ) {
    if (this.disposed) return;
    const tagState = this.tagState.get(elementTagId);
    if (isNil(tagState) || isNil(tagState.head)) return;

    const newHead = clientToWorldOnCameraPlane(
      this.viewer,
      clientX - grabOffsetX,
      clientY - grabOffsetY,
      tagState.head,
    );
    this.tagState.set(elementTagId, this.updateTagDragging(tagState, false));
    this.viewer.setNavigationLock(false);
    // Since this communicates with the element tags store which will trigger a re-render,
    // we don't need to update the visuals here.
    this.elementTagsStore.commitPlacement(elementTagId, toPoint3D(newHead));
  }

  /**
   * Called when a drag is canceled (e.g. `pointercancel`).
   * Restores leaders and unlocks navigation without persisting.
   */
  public onLabelDragCancel(elementTagId: AutodeskElementTagId) {
    if (this.disposed) return;
    const tagState = this.tagState.get(elementTagId);
    if (isNil(tagState)) return;

    // Render from the *next* state — `tagState` still has `isDragging: true`, which suppresses the
    // leader, so reusing it here would leave the leader hidden until the next camera change.
    const nextTagState = this.updateTagDragging(tagState, false);
    this.tagState.set(elementTagId, nextTagState);
    this.viewer.setNavigationLock(false);
    this.updateTagVisuals(nextTagState);
    this.republishTags();
  }
  //endregion Public API drag lifecycle

  /**
   * Tears down all viewer resources. Safe to call once; a second call no-ops.
   * Sets `disposed` last, so this method's own cleanup (which calls `removeTag`) isn't
   * blocked by the guard it installs for every other public method.
   */
  dispose() {
    if (this.disposed) return;
    for (const tagState of this.tagState.values()) {
      this.removeTagState(tagState);
    }
    this.tagState.clear();
    this.propertyCache.clear();
    this.lookedUpPropertyKeys.clear();
    this.renderedTagsStore.setTags([]);
    this.cleanUpFns.forEach((fn) => fn());
    this.cleanUpFns = [];
    this.leaderMaterial.dispose();
    this.leaderGeometry.dispose();
    this.viewer.impl.clearOverlay(TAG_SCENE_NAME);
    this.disposed = true;
  }
}
